/**
 * KAN-877: адресаты звонка клиенту (GET call-targets) и выбор адресата.
 *
 * useCallTargets - для карточки одного клиента: грузит сразу.
 * useCallTargetPicker - для строк списков: грузит только по клику; один
 * человек карточки (client/referral) - звонок сразу, несколько или единственный другой адресат -
 * выбор; ошибка - тост без звонка (иначе сервер мог бы набрать не того);
 * пусто - звонок по правилу сервера (без target), сервер скажет причину.
 */
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CALL_TARGETS_ERROR_TOAST, callTargetsNeedChoice } from '@/lib/callTargets';
import { fetchCallTargets, type CallTarget, type CallTargetKind } from '@/services/api/callTargets';

/** Адресаты меняются редко (ЛПР записывает бот или менеджер), минуты хватает. */
export const CALL_TARGETS_STALE_MS = 60_000;

export function callTargetsQueryKey(clientId: string) {
  return ['callTargets', clientId] as const;
}

export function useCallTargets(clientId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CallTarget[]>({
    queryKey: callTargetsQueryKey(clientId),
    queryFn: () => fetchCallTargets(clientId),
    staleTime: CALL_TARGETS_STALE_MS,
    // Без адресатов кнопка ждёт: одна короткая повторная попытка, дальше "Повторить".
    retry: 1,
    retryDelay: 300,
    enabled: enabled && !!clientId,
  });
}

export interface CallTargetPicker {
  /** Адресаты в открытом выборе; null - выбор закрыт. */
  choices: CallTarget[] | null;
  loading: boolean;
  start: () => Promise<void>;
  choose: (target: CallTargetKind) => void;
  close: () => void;
}

export function useCallTargetPicker(clientId: string, call: (target?: CallTargetKind) => void): CallTargetPicker {
  const queryClient = useQueryClient();
  const [choices, setChoices] = useState<CallTarget[] | null>(null);
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setLoading(true);
    let targets: CallTarget[];
    try {
      targets = await queryClient.fetchQuery({
        queryKey: callTargetsQueryKey(clientId),
        queryFn: () => fetchCallTargets(clientId),
        staleTime: CALL_TARGETS_STALE_MS,
        // По клику ждёт человек: без повторов, при сбое сразу тост, звонка нет.
        retry: false,
      });
    } catch {
      toast.error(CALL_TARGETS_ERROR_TOAST);
      return;
    } finally {
      setLoading(false);
    }
    if (callTargetsNeedChoice(targets)) setChoices(targets);
    else call(targets[0]?.target);
  };

  const choose = useCallback(
    (target: CallTargetKind) => {
      setChoices(null);
      call(target);
    },
    [call]
  );
  // Стабильная ссылка: выбор подписывается на Escape по ней.
  const close = useCallback(() => setChoices(null), []);

  return { choices, loading, start, choose, close };
}
